import axios from 'axios';

import {
  BannerCompact,
  BaseSizeOption,
  Export,
  ExportFilterModifier,
  QuestionBase,
} from '../../types/domainModels';
import { getAPIUrl } from './baseAPI';

export type SaveBannerOption =
  // Idea presenter
  | { conceptId: { id: number } }

  // Matrix
  | { matrixOptionId: number; enumValue: { id: number | undefined } }
  // Multiple choice
  | {
      id: number;
    }
  // Number questions (like age demographic)
  | {
      highOption: number | '';
      lowOption: number | '';
    }
  // Ranking & scale
  | {
      enumValue: number | null;
      highOption: number | '';
      lowOption: number | '';
    };

// Exactly one of the two must be set — the backend connects the metric to either a
// Question or a calculated SurveyVariable depending on which is present.
export type SaveBannerQuestionOrVariableRef =
  | { questionId: number; variableId?: never }
  | { questionId?: never; variableId: number };

// This body is allowed to have undefined values in it. The API is flexible to allow you
// to save an export in any state.
export interface SaveExportBannersBody {
  averages: SaveBannerQuestionOrVariableRef[];
  bannerId: number | null;
  bottomBox: { questionId: number; value: number }[];
  categories: {
    base: boolean;
    categories: {
      attributes: {
        logicalModifier: ExportFilterModifier | null;
        options?: SaveBannerOption[];
        segments?: { id: number }[];
        waves?: { id: number }[];
      }[];
      concept?: { id: number } | null;
      question?: { id: number };
      variableId?: number;
    }[];
    title: string;
  }[];
  confidenceLevel: number | null;
  excludeQuestionIds: number[];
  exposedToQuestionIds: number[];
  includeFreeTextTab: boolean;
  variableBaseSizes?: Record<number, BaseSizeOption>;
  includeSummaryTab: boolean;
  stackComplexQuestions: boolean;
  surveyId: number;
  title: string;
  topBox: { questionId: number; value: number }[];
}

type Logic = 'is' | 'isnt' | 'should';
export type IdeaPresenterCondition = {
  conceptId?: number;
  id: number;
  logic: Logic;
  response: number[];
  type: 'ideaPresenter';
};
export type MatrixCondition = {
  conceptId?: number;
  id: number;
  logic: Logic;
  response: Record<string, string[]>;
  type: 'matrixV2';
};
export type MultipleChoiceCondition = {
  conceptId?: number;
  id: number;
  logic: Logic;
  response: number[];
  type: 'multipleChoice';
};
export type NumberCondition = {
  conceptId?: number;
  id: number;
  logic: Logic;
  response: [number, number];
  type: 'number';
};
export type RankingScaleCondition = {
  conceptId?: number;
  id: number;
  logic: Logic;
  response: Record<string, [number, number][]>;
  type: 'ranking' | 'scale';
};
type QuestionCondition =
  | IdeaPresenterCondition
  | MatrixCondition
  | MultipleChoiceCondition
  | NumberCondition
  | RankingScaleCondition;
export type SegmentCondition = { ids: number[]; logic: Logic };
export type WaveCondition = { ids: number[]; logic: Logic };

type ConditionsGroup = {
  conditions: {
    groupLogic: 'and' | 'or';
    groups: (
      | {
          data: QuestionCondition;
          type: 'question';
        }
      | {
          data: SegmentCondition;
          type: 'segment';
        }
      | {
          data: WaveCondition;
          type: 'wave';
        }
    )[][];
  };
  title: string;
}[];

export interface CreateExcelExportBody {
  banners: ConditionsGroup;
  calculatedMetrics: Record<
    string,
    {
      average?: boolean;
      base?: QuestionBase;
      bottomBox?: number;
      topBox?: number;
    }
  >;
  customVariableSettings?: Record<
    string,
    { average?: boolean; base?: BaseSizeOption }
  >;
  filters: ConditionsGroup;
  includeFreeTextTab?: boolean;
  includeSummaryTab?: boolean;
  meta: {
    exportedAt: string;
    savedExportName?: string;
  };
  output: {
    excludeQuestionIds: number[];
    multiDimensionalQuestions: 'stack' | 'stretch';
  };
  statTesting?: { confidenceInterval: number };
}

export const CREATE_EXCEL_EXPORT = ({ surveyId }: { surveyId: number }) => ({
  path: `/surveys/${surveyId}:exportExcel`,
  version: 'v2' as const,
});

export async function createExcelExport({
  data,
  surveyId,
}: {
  data: CreateExcelExportBody;
  surveyId: number;
}) {
  return (
    await axios.post<undefined>(
      getAPIUrl(CREATE_EXCEL_EXPORT({ surveyId })),
      data,
    )
  ).data;
}

export const CREATE_POWERPOINT_EXPORT = ({
  surveyId,
}: {
  surveyId: number;
}) => ({
  path: `/surveys/${surveyId}:exportPowerPoint`,
  version: 'v2' as const,
});

export async function createPowerPointExport({
  data,
  surveyId,
}: {
  data: CreateExcelExportBody;
  surveyId: number;
}) {
  return (
    await axios.post<undefined>(
      getAPIUrl(CREATE_POWERPOINT_EXPORT({ surveyId })),
      data,
    )
  ).data;
}

export const CREATE_RAW_DATA_EXPORT = ({ surveyId }: { surveyId: number }) => ({
  path: `/surveys/${surveyId}:exportRaw`,
  version: 'v2' as const,
});

export async function createRawDataExport({
  respondentType,
  surveyId,
  waveIds,
}: {
  respondentType: 'active' | 'all';
  surveyId: number;
  waveIds?: number[];
}) {
  return (
    await axios.post<undefined>(
      getAPIUrl(CREATE_RAW_DATA_EXPORT({ surveyId })),
      { respondentType, waveIds },
    )
  ).data;
}

export const CREATE_SINGLE_SURVEY_DETAIL_EXPORT = ({
  surveyId,
}: {
  surveyId: number;
}) => ({
  path: `/surveys/${surveyId}:exportSingleSurveyDetail`,
  version: 'v2' as const,
});

export async function createSingleSurveyDetailExport({
  surveyId,
  waveIds,
}: {
  surveyId: number;
  waveIds?: number[];
}) {
  return (
    await axios.post<undefined>(
      getAPIUrl(CREATE_SINGLE_SURVEY_DETAIL_EXPORT({ surveyId })),
      {
        waveIds,
      },
    )
  ).data;
}

export const CREATE_SPSS_EXPORT = ({ surveyId }: { surveyId: number }) => ({
  path: `/surveys/${surveyId}:exportSPSS`,
  version: 'v2' as const,
});

export async function createSPSSExport({
  surveyId,
  waveIds,
  version,
}: {
  surveyId: number;
  waveIds?: number[];
  version: 1 | 2;
}) {
  return (
    await axios.post<undefined>(getAPIUrl(CREATE_SPSS_EXPORT({ surveyId })), {
      waveIds,
      version,
    })
  ).data;
}

export const DELETE_SAVED_EXPORT = ({
  savedExportId,
}: {
  savedExportId: number;
}) => ({
  path: `/surveys/results/banner/${savedExportId}`,
  version: 'v1' as const,
});

export async function deleteSavedExport({
  savedExportId,
}: {
  savedExportId: number;
}) {
  return (
    await axios.delete<void>(getAPIUrl(DELETE_SAVED_EXPORT({ savedExportId })))
  ).data;
}

export const DOWNLOAD_EXPORT = ({ id }: { id: number }) => ({
  path: `/exports/${id}:download`,
  version: 'v2' as const,
});

export function downloadExport({ id }: { id: number }) {
  return axios.get(getAPIUrl(DOWNLOAD_EXPORT({ id })), {
    responseType: 'blob',
  });
}

export const EDIT_EXPORT = ({ exportId }: { exportId: number }) => ({
  path: `/exports/${exportId}`,
  version: 'v2' as const,
});

export async function editExport({
  data,
  exportId,
}: {
  data: Partial<{ displayStatus: Export['displayStatus'] }>;
  exportId: number;
}) {
  return (await axios.patch<Export>(getAPIUrl(EDIT_EXPORT({ exportId })), data))
    .data;
}

export const FETCH_EXPORTS = ({ surveyHash }: { surveyHash: string }) => ({
  path: `/surveys/${surveyHash}/exports`,
  version: 'v2' as const,
});

export async function fetchExports({
  showArchived,
  surveyHash,
}: {
  showArchived: boolean;
  surveyHash: string;
}) {
  return (
    await axios.get<Export[]>(getAPIUrl(FETCH_EXPORTS({ surveyHash })), {
      params: { includeArchived: showArchived ? 'true' : undefined },
    })
  ).data;
}

export const SAVE_EXPORT_BANNERS = ({ surveyId }: { surveyId: number }) => ({
  path: `/surveys/results/banner/${surveyId}`,
  version: 'v1' as const,
});

export async function saveExportBanners({
  data,
  surveyId,
}: {
  data: SaveExportBannersBody;
  surveyId: number;
}) {
  return (
    await axios.post<BannerCompact[]>(
      getAPIUrl(SAVE_EXPORT_BANNERS({ surveyId })),
      data,
    )
  ).data;
}
